import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  CellLeftV8,
  order2Sorting,
  CellCenterV8,
  CellDateTimeV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { AlarmTypeValues } from 'shared-pages/shared/constants';
import { formatDateTime } from 'shared-pages/utils/helpers';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Idoc'), { id: row.original.idocId }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const actionClicked = useCallback(
    (row) => {
      history.push({
        pathname: row.link,
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  function getApprovalStatus(row: any): boolean {
    switch (row.original.alarmTypeId) {
      case AlarmTypeValues.CollectionVehicle:
        return row.original.vehicle?.isApproved ?? false;
      case AlarmTypeValues.CollectionDriver:
      case AlarmTypeValues.ADR:
        return row.original.driver?.isApproved ?? false;
      case AlarmTypeValues.CollectionPermit:
      case AlarmTypeValues.CollectorInsurance:
      case AlarmTypeValues.SAMEE_Agreement:
      case AlarmTypeValues.SAMEE_Certificate:
        return row.original.collector?.isApproved ?? false;
      case AlarmTypeValues.SecondaryStorage_AEPO:
      case AlarmTypeValues.SecondaryStorage_Insurance:
      case AlarmTypeValues.RecyclingCompany_AEPO:
      case AlarmTypeValues.RecyclerInsurance:
      case AlarmTypeValues.RecyclerISO:
        return row.original.wastereceptionpoint?.isApproved ?? false;
      default:
        return false; // Default case if the ID is not recognized
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'composedDate',
        header: t('Ημερομηνία Σύνταξης'),
        columnName: t('Ημερομηνία Σύνταξης'),
        accessorKey: 'composedDate',
        orderByField: 'composedDate',
        cell: CellDateTimeV8,
        size: 10,
        meta: { type: 'text' },
      },
      {
        id: 'sendDate',
        header: t('Ημερομηνία Αποστολής'),
        columnName: t('Ημερομηνία Αποστολής'),
        accessorKey: 'sendDate',
        orderByField: 'sendDate',
        cell: CellDateTimeV8,
        size: 10,
        meta: { type: 'text' },
      },
      {
        id: 'originatedFrom',
        header: t('Προέλευση'),
        columnName: t('Προέλευση'),
        accessorKey: 'originatedFrom',
        orderByField: 'originatedFrom',
        cell: ({ row }: any) => {
          return (
            <div>
              {row.original.originatedFrom}
              <div className="text-muted mt-2">
                Αποστολέας: <strong>{row.original.from}</strong>
              </div>
            </div>
          );
        },
        size: 5,
      },
      {
        id: 'to',
        header: t('Παραλήπτης'),
        columnName: t('Παραλήπτης'),
        accessorKey: 'to',
        orderByField: 'to',
        cell: CellLeftV8,
        size: 5,
      },
      {
        id: 'subjectHtml',
        header: t('Θέμα / Περιεχόμενο'),
        columnName: t('Θέμα / Περιεχόμενο'),
        accessorKey: 'subject',
        orderByField: 'subject',
        cell: (par: any) => {
          const { row } = par;
          const subject = row.original.subject;
          const html = row.original.html;
          return (
            <div className="text-start" style={{ maxWidth: '450px' }}>
              {subject && (
                <div className="text-center" style={{ fontWeight: 500, marginBottom: '6px' }}>
                  {String(subject)}
                </div>
              )}
              {html && (
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: html }} className="text-start"></div>
                </div>
              )}
            </div>
          );
        },
        size: 40,
      },

      {
        id: 'failed',
        header: t('Αποτυχία Αποστολής'),
        columnName: t('Αποτυχία Αποστολής'),
        accessorKey: 'failedReason',
        orderByField: 'failedDate',
        cell: (par: any) => {
          const { row } = par;
          const failedDate = row.original.failedDate;
          const failedReason = row.original.failedReason;
          const failedDetails = row.original.failedDetails;
          const isDeleted = row.original.isDeleted;
          if (!failedDate && !failedReason && !failedDetails && !isDeleted) return <></>;

          return (
            <div className="text-start" style={{ maxWidth: '450px' }}>
              {Boolean(isDeleted) && <div className="text-danger">{t('Ακυρώθηκε η Αποστολή')}</div>}
              {failedDate && (
                <div style={{ fontWeight: 600 }}>
                  <span className="text-danger">{t('Αποτυχία Αποστολής')}</span>
                  <span className="text-muted">&nbsp;({formatDateTime(failedDate)})</span>
                </div>
              )}
              {failedReason && (
                <div style={{ fontWeight: 400, marginBottom: '3px', marginTop: '3px' }}>
                  <strong className="text-muted">{String(failedReason)}</strong>
                </div>
              )}
              {failedDetails && (
                <div
                  className="text-muted"
                  style={{
                    marginTop: '3px',
                    maxHeight: '120px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {String(failedDetails)}
                </div>
              )}
            </div>
          );
        },
        size: 35,
      },
      /*
      {
        id: 'text',
        header: t('Περιεχόμενο (text)'),
        columnName: t('Περιεχόμενο (text)'),
        accessorKey: 'text',
        orderByField: 'text',
        cell: (par: any) => {
          const { getValue, row } = par;
          return (
            <div
              className="text-start"
              style={{ maxHeight: '200px', maxWidth: '300px', overflow: 'scroll' }}
            >
              {row.original.text}
            </div>
          );
        },
        size: 35,
      },
      */
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="mails-grid"
          appStore={appStore}
          tableStore={tableStore as any}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
